import React from 'react';
import { makeStyles } from 'tss-react/mui';
import Timeline from '@mui/lab/Timeline';
import TimelineItem from '@mui/lab/TimelineItem';
import TimelineSeparator from '@mui/lab/TimelineSeparator';
import TimelineConnector from '@mui/lab/TimelineConnector';
import TimelineContent from '@mui/lab/TimelineContent';
import TimelineDot from '@mui/lab/TimelineDot';
import SvgInfo from '@HomaHealth/ui-component-library/lib/_AIQ-components/atoms/Icons/Info';
import {
  CardComponent,
  DottedText,
  InformationIcon,
} from '@HomaHealth/ui-component-library/lib/_AIQ-components';
import { Grid } from '@mui/material';
import { Typography } from '@HomaHealth/ui-component-library/lib/_patient-access-components';
import { patientAccessTheme } from '@HomaHealth/ui-component-library';

const useStyles = makeStyles()((theme) => ({
  customTimelineItem: {
    '&.MuiTimelineItem-missingOppositeContent::before': {
      flex: 'unset',
      content: '""',
      padding: 0,
    },
    alignItems: 'stretch',
  },
  paper: {
    padding: '6px 16px',
  },
  secondaryTail: {
    backgroundColor: theme.palette.secondary.main,
  },
  dotContainer: {
    '&.MuiTimelineDot-root': {
      // border: 'none',
      boxShadow: 'none',
      margin: '0px',
    },
    backgroundColor: '#EDEFF7',
  },
  dotSecContainer: {
    '&.MuiTimelineDot-root': {
      // border: 'none',
      boxShadow: 'none',
      margin: '0px',
    },
    backgroundColor: '#E8F6F5',
  },
  iconColor: {
    '& path': {
      fill: '#17A39C',
    },
  },
  descriptionContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  sourceContainer: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  mainTable: {
    marginTop: '10px',
  },
}));

interface InsightsPopupProps {
  data: any;
  children: React.ReactNode;
  insightsDiscovered: number;
  insightsTitle?: string;
  insightIcon?: React.JSX.Element;
}

const InsightsPopup: React.FC<InsightsPopupProps> = ({
  data,
  children,
  insightsDiscovered,
  insightsTitle,
  insightIcon,
}) => {
  const { classes } = useStyles();
  return (
    <Timeline>
      <TimelineItem className={classes.customTimelineItem}>
        <TimelineSeparator>
          <TimelineDot className={classes.dotContainer}>
            <SvgInfo width={'20px'} height={'20px'} />
          </TimelineDot>
          <TimelineConnector />
        </TimelineSeparator>
        <TimelineContent>
          <Grid>
            <Typography
              fontSize="12px"
              textColor={patientAccessTheme.palette.text.mediumemphasis}
              fontType="medium"
            >
              Notification Summary
            </Typography>
            <CardComponent
              mainGridStyles={{
                backgroundColor: `${patientAccessTheme.palette.grey[100]}`,
                marginTop: '10px',
              }}
              title={''}
              body={
                <Grid className={classes.descriptionContainer}>
                  <Typography fontSize="16px">{data?.title}</Typography>
                  <Typography
                    fontSize="14px"
                    textColor={patientAccessTheme.palette.text.mediumemphasis}
                  >
                    {data?.title} Here{' '}
                    {insightsDiscovered === 1 ? 'is the' : 'are the'}{' '}
                    {insightsDiscovered === 1
                      ? insightsTitle === 'Suggested Task'
                        ? 'suggested task '
                        : 'insight '
                      : insightsTitle === 'Suggested Task'
                      ? 'suggested tasks '
                      : 'insights '}
                    that {insightsDiscovered === 1 ? 'has' : 'have'} been found.
                  </Typography>
                  <Grid className={classes.sourceContainer}>
                    <Typography
                      fontSize="12px"
                      textColor={patientAccessTheme.palette.text.mediumemphasis}
                    >
                      Source:{' '}
                      <DottedText
                        typographyStyles={{
                          fontSize: '12px',
                          fontWeight: 600,
                          color: `${patientAccessTheme.palette.primary.main}`,
                        }}
                        text={data?.source}
                      />
                    </Typography>
                    <Typography
                      fontSize="14px"
                      textColor={`${patientAccessTheme.palette.AIQChips?.textColor3}`}
                    >
                      {data?.newDate}
                    </Typography>
                  </Grid>
                </Grid>
              }
            />
          </Grid>
        </TimelineContent>
      </TimelineItem>
      <TimelineItem className={classes.customTimelineItem}>
        <TimelineSeparator>
          <TimelineDot
            className={classes.dotSecContainer}
            style={{
              backgroundColor:
                insightsTitle === 'Suggested Task' ? '#F5A4A533' : '#E8F6F5',
            }}
          >
            {insightIcon || (
              <InformationIcon
                width={'17px'}
                height={'16px'}
                className={classes.iconColor}
              />
            )}
          </TimelineDot>
          <TimelineConnector />
        </TimelineSeparator>
        <TimelineContent>
          <Typography
            fontSize="12px"
            textColor={
              insightsTitle === 'Suggested Task' ? '#E08083' : '#17A39C'
            }
            fontType="medium"
          >
            {insightsDiscovered}{' '}
            {insightsDiscovered === 1
              ? insightsTitle || 'Insight discovered'
              : insightsTitle === 'Suggested Task'
              ? 'Suggested Tasks'
              : insightsTitle
              ? 'Insights discovered'
              : 'Insights discovered'}
          </Typography>
          <Grid className={classes.mainTable}>{children}</Grid>
        </TimelineContent>
      </TimelineItem>
    </Timeline>
  );
};

export default InsightsPopup;
